import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  Grid,
  IconButton,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tooltip,
  TextField,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { useDropzone } from 'react-dropzone';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DialogTransition from './Common/DialogTransition';
import useOrganization from '../hooks/UseOrganization';
import REGEX from '../const/Regex';
import SwalToast from './Common/SwalToast';

const AddProductDialog = ({ open = false, handleClose }) => {
  const theme = useTheme();
  const { createProductList } = useOrganization();
  const [fileContent, setFileContent] = useState({});
  const [uploadedFile, setUploadedFile] = useState(undefined);
  const initialValues = {
    productName: '',
  };
  const validationSchema = object({
    productName: string()
      .matches(REGEX.ORGANIZATION_NAME, 'Product name is not valid.')
      .min(2, 'Product name must be at least 2 characters')
      .max(150, 'Product name must be at most 150 characters')
      .required('Required'),
  });
  const isFlatObject = (obj) => {
    if (typeof obj !== 'object' || obj === null) {
      return false;
    }
    return !Object.values(obj).some(
      (value) => typeof value === 'object' && value !== null
    );
  };

  const handleOnDropChange = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        SwalToast({
          icon: 'error',
          title: 'Invalid JSON file.',
        });
      }
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setUploadedFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const json = JSON.parse(e.target.result);
            const isFlat = isFlatObject(json);
            if (isFlat) {
              setFileContent(json);
            } else {
              SwalToast({
                icon: 'error',
                title: 'Invalid JSON file.',
              });
              setUploadedFile(undefined);
            }
          } catch (error) {
            SwalToast({
              icon: 'error',
              title: 'Invalid JSON file.',
            });
            setUploadedFile(undefined);
          }
        };
        reader.readAsText(file);
      }
    },
    [setUploadedFile]
  );
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/json': ['.json'],
    },
    maxFiles: 1,
    onDrop: handleOnDropChange,
  });

  const formikForm = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {
      const payload = {
        title: formikForm?.values?.productName,
        permission: fileContent || {},
      };
      createProductList(payload);
      handleClose();
    },
  });

  return (
    <Dialog
      onClose={handleClose}
      maxWidth="sm"
      fullWidth={true}
      TransitionComponent={DialogTransition}
      open={open}
    >
      <DialogTitle>Create Product</DialogTitle>
      <IconButton
        onClick={handleClose}
        size="large"
        sx={{ position: 'absolute', right: 16, top: 8 }}
      >
        <CloseIcon style={{ color: theme.palette.other.icon }} />
      </IconButton>
      <DialogContent dividers>
        <Grid container justifyContent="center" textAlign="center">
          <Grid
            item
            {...getRootProps({ className: 'dropzone' })}
            id="dm_di_import_dts_all_type_fa_drop_box"
            sx={{
              border: `1px dashed ${theme.palette.primary.main} !important `,
              pt: '27px !important',
              borderRadius: '4px',
            }}
            width="80%"
            height={200}
          >
            <UploadFileIcon
              sx={{ width: '60px', height: '60px', color: 'primary.main' }}
            />
            <br />
            <br />

            <input {...getInputProps()} hidden />
            <Grid container alignItems="center" justifyContent="center">
              {uploadedFile === undefined ? (
                <Box>
                  <Grid display="flex" flexDirection="row">
                    <Grid mr={1}>
                      <Typography variant="body2">
                        Drag and drop or choose a file to upload
                      </Typography>
                    </Grid>
                    <Grid mt={0.3}>
                      <Tooltip
                        title={
                          <Typography variant="body4">
                            Only flat json file allowed.
                          </Typography>
                        }
                        placement="right-end"
                        arrow
                      >
                        <HelpOutlineIcon
                          sx={{
                            height: '14px',
                            width: '14px',
                            color: 'primary.main',
                          }}
                        />
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Grid>
                  <Typography variant="body2">{uploadedFile?.name}</Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid
            width="100%"
            item
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            height="84px"
            marginTop={2}
          >
            <Box width="30%">
              <Typography variant="h5">Product name*</Typography>
            </Box>
            <Box width="60%">
              <TextField
                placeholder="Enter product name"
                name="productName"
                value={formikForm?.values?.productName}
                onChange={(e) => formikForm.handleChange(e)}
                aria-label="productname"
                onBlur={formikForm?.handleBlur}
                helperText={
                  formikForm?.errors?.productName &&
                  formikForm?.touched?.productName
                    ? formikForm?.errors?.productName
                    : null
                }
                InputProps={{
                  endAdornment: (
                    <Tooltip
                      title={
                        <Typography>
                          - Product name should start with a character. <br />
                          - Numbers (0-9) are allowed. <br />- Underscore(_) and
                          hyphen (-) are allowed.
                        </Typography>
                      }
                      arrow
                      placement="top"
                    >
                      <InfoOutlinedIcon />
                    </Tooltip>
                  ),
                }}
                error={
                  Boolean(formikForm?.errors?.productName) &&
                  formikForm?.touched?.productName
                }
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={formikForm.handleSubmit}>
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddProductDialog.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default AddProductDialog;
